/**
 * Pure form logic for the SSO provider page — payload assembly, validation and
 * URL derivation. Extracted from SsoProviderPage so it can be unit-tested
 * without rendering the (large, hook-heavy) component.
 */
import type {
  CreateSsoProviderPayload,
  SsoProvider,
  SsoProviderType,
  UpdateSsoProviderPayload,
} from "@/types";

export interface RoleMappingRow {
  claimValue: string;
  roleNames: string[];
}

export interface FormState {
  provider_type: SsoProviderType;
  provider_key: string;
  display_name: string;
  client_id: string;
  client_secret: string;
  tenant_id: string;
  server_metadata_url: string;
  scopes: string;
  redirect_uri: string;
  allowed_email_domains: string;
  is_enabled: boolean;
  display_order: number;
  target_audience: "client" | "provider";
  role_claim: string;
  role_mappings: RoleMappingRow[];
  default_roles: string[];
  // SAML 2.0
  idp_entity_id: string;
  idp_sso_url: string;
  idp_x509_cert: string;
  attr_email: string;
  attr_name: string;
  attr_first_name: string;
  attr_last_name: string;
  want_assertions_signed: boolean;
}

export type SectionKey = "provider" | "credentials" | "access" | "roles";

export const emptyForm: FormState = {
  provider_type: "google",
  provider_key: "",
  display_name: "",
  client_id: "",
  client_secret: "",
  tenant_id: "",
  server_metadata_url: "",
  scopes: "openid email profile",
  redirect_uri: "",
  allowed_email_domains: "",
  is_enabled: true,
  display_order: 0,
  target_audience: "client",
  role_claim: "groups",
  role_mappings: [],
  default_roles: [],
  idp_entity_id: "",
  idp_sso_url: "",
  idp_x509_cert: "",
  attr_email: "",
  attr_name: "",
  attr_first_name: "",
  attr_last_name: "",
  want_assertions_signed: true,
};

// ---------------------------------------------------------------------------
// URL derivation
// ---------------------------------------------------------------------------

export function apiBase(): string {
  let base = import.meta.env.VITE_API_BASE_URL || "/api/v1/";
  if (base.startsWith("/")) {
    base = window.location.origin + base;
  }
  if (!base.endsWith("/")) base += "/";
  return base;
}

export function buildRedirectUri(providerKey: string): string {
  return `${apiBase()}user-service/auth/sso/${providerKey}/callback`;
}

/** SAML SP ACS / metadata URLs the IdP team registers (portal-scoped). */
export function buildSamlAcsUrl(portalId: string, providerKey: string): string {
  return `${apiBase()}user-service/auth/sso/saml/${portalId}/${providerKey}/acs`;
}

export function buildSamlMetadataUrl(portalId: string, providerKey: string): string {
  return `${apiBase()}user-service/auth/sso/saml/${portalId}/${providerKey}/metadata`;
}

/** SP metadata URL derived from the stored ACS (.../acs -> .../metadata). */
export function deriveSamlMetadataUrl(
  redirectUri: string,
  portalId?: string,
  providerKey?: string,
): string {
  if (redirectUri) {
    return redirectUri.endsWith("/acs")
      ? redirectUri.slice(0, -"/acs".length) + "/metadata"
      : redirectUri;
  }
  return portalId && providerKey ? buildSamlMetadataUrl(portalId, providerKey) : "";
}

// ---------------------------------------------------------------------------
// Hydration
// ---------------------------------------------------------------------------

export const providerFromRecord = (provider: SsoProvider): FormState => ({
  provider_type: provider.provider_type,
  provider_key: provider.provider_key,
  display_name: provider.display_name,
  client_id: provider.client_id ?? "",
  client_secret: "",
  tenant_id: provider.tenant_id ?? "",
  server_metadata_url: provider.server_metadata_url ?? "",
  scopes: provider.scopes,
  redirect_uri: provider.redirect_uri ?? "",
  allowed_email_domains: provider.allowed_email_domains ?? "",
  is_enabled: provider.is_enabled,
  display_order: provider.display_order,
  target_audience: provider.target_audience ?? "client",
  role_claim: provider.role_claim ?? "groups",
  role_mappings: provider.role_mapping
    ? Object.entries(provider.role_mapping).map(([claimValue, roleNames]) => ({
        claimValue,
        roleNames: roleNames as string[],
      }))
    : [],
  default_roles: provider.default_roles ?? [],
  idp_entity_id: provider.idp_entity_id ?? "",
  idp_sso_url: provider.idp_sso_url ?? "",
  idp_x509_cert: provider.idp_x509_cert ?? "",
  attr_email: provider.attribute_mapping?.email ?? "",
  attr_name: provider.attribute_mapping?.name ?? "",
  attr_first_name: provider.attribute_mapping?.first_name ?? "",
  attr_last_name: provider.attribute_mapping?.last_name ?? "",
  want_assertions_signed: provider.want_assertions_signed ?? true,
});

// ---------------------------------------------------------------------------
// Payload assembly
// ---------------------------------------------------------------------------

export function buildRoleFields(form: FormState) {
  const roleMappingObj: Record<string, string[]> = {};
  for (const row of form.role_mappings) {
    if (row.claimValue.trim() && row.roleNames.length > 0) {
      roleMappingObj[row.claimValue.trim()] = row.roleNames;
    }
  }
  return {
    role_claim: form.role_claim || null,
    role_mapping: Object.keys(roleMappingObj).length > 0 ? roleMappingObj : null,
    default_roles: form.default_roles.length > 0 ? form.default_roles : null,
  };
}

export function buildSamlFields(form: FormState) {
  const mapping: Record<string, string> = {};
  if (form.attr_email.trim()) mapping.email = form.attr_email.trim();
  if (form.attr_name.trim()) mapping.name = form.attr_name.trim();
  if (form.attr_first_name.trim()) mapping.first_name = form.attr_first_name.trim();
  if (form.attr_last_name.trim()) mapping.last_name = form.attr_last_name.trim();
  if (form.role_claim.trim()) mapping.groups = form.role_claim.trim();
  return {
    idp_entity_id: form.idp_entity_id.trim() || null,
    idp_sso_url: form.idp_sso_url.trim() || null,
    idp_x509_cert: form.idp_x509_cert.trim() || null,
    attribute_mapping: Object.keys(mapping).length > 0 ? mapping : null,
    want_assertions_signed: form.want_assertions_signed,
  };
}

export function buildCreatePayload(form: FormState): CreateSsoProviderPayload {
  const isSaml = form.provider_type === "saml";
  const payload: CreateSsoProviderPayload = {
    provider_type: form.provider_type,
    provider_key: form.provider_key,
    display_name: form.display_name,
    allowed_email_domains: form.allowed_email_domains || null,
    is_enabled: form.is_enabled,
    display_order: form.display_order,
    target_audience: form.target_audience,
    ...buildRoleFields(form),
  };
  if (isSaml) {
    Object.assign(payload, buildSamlFields(form));
    payload.redirect_uri = form.redirect_uri;
  } else {
    payload.client_id = form.client_id;
    payload.client_secret = form.client_secret;
    payload.tenant_id = form.tenant_id || null;
    payload.server_metadata_url = form.server_metadata_url || null;
    payload.scopes = form.scopes;
    payload.redirect_uri = form.redirect_uri;
  }
  return payload;
}

export function buildUpdatePayload(form: FormState): UpdateSsoProviderPayload {
  const isSaml = form.provider_type === "saml";
  const payload: UpdateSsoProviderPayload = {
    display_name: form.display_name,
    allowed_email_domains: form.allowed_email_domains || null,
    is_enabled: form.is_enabled,
    display_order: form.display_order,
    target_audience: form.target_audience,
    ...buildRoleFields(form),
  };
  if (isSaml) {
    Object.assign(payload, buildSamlFields(form));
    payload.redirect_uri = form.redirect_uri;
  } else {
    payload.client_id = form.client_id;
    payload.tenant_id = form.tenant_id || null;
    payload.server_metadata_url = form.server_metadata_url || null;
    payload.scopes = form.scopes;
    payload.redirect_uri = form.redirect_uri;
    if (form.client_secret) {
      payload.client_secret = form.client_secret;
    }
  }
  return payload;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export function computeSectionErrors(
  form: FormState,
  isEdit: boolean,
): Record<SectionKey, number> {
  const isSaml = form.provider_type === "saml";
  return {
    provider:
      (isEdit ? 0 : form.provider_key ? 0 : 1) +
      (form.display_name ? 0 : 1) +
      (form.redirect_uri ? 0 : 1),
    credentials: isSaml
      ? (form.idp_entity_id ? 0 : 1) +
        (form.idp_sso_url ? 0 : 1) +
        (form.idp_x509_cert ? 0 : 1)
      : (form.client_id ? 0 : 1) +
        (isEdit ? 0 : form.client_secret ? 0 : 1) +
        (form.provider_type === "microsoft" && !form.tenant_id ? 1 : 0) +
        (form.provider_type === "oidc" && !form.server_metadata_url ? 1 : 0),
    access: 0,
    roles: 0,
  };
}

export function totalSectionErrors(errors: Record<SectionKey, number>): number {
  return errors.provider + errors.credentials + errors.access + errors.roles;
}
